/**
 * Caption ROW SELECTION — the pure heart of `src/remotion/CaptionLine.tsx`,
 * extracted on 2026-09-17 (spec measured-caption-fit §3.1) so the renderer
 * and the fit gate (`measuredFit.ts`) decide rows with ONE implementation.
 * Every branch below is the founding expression, moved verbatim: the
 * linePunch re-wrap, the pinned `wrapCueTotalToWidth`, the unpinned
 * `wrapCuePreferring`, the non-auto `wrapUniformCue`, the legacy one-line
 * collapse, `rowsWithScaledLastLine`, and the bigPunch per-line wraps.
 *
 * PURE — no React, no DOM. The measurer factory is injected: CaptionLine
 * passes canvas-or-estimate (fonts-gated), the server passes font-file
 * metrics (`fontMetrics.ts`), the Studio passes canvas. Same inputs ⇒ same
 * rows, by construction — a parity test pins the founding outputs.
 */
import {
  rowsWithScaledLastLine,
  uniformMaxChars,
  wrapCuePreferring,
  wrapCueToWidth,
  wrapCueTotalToWidth,
  wrapUniformCue,
  type MeasureFn,
} from "./captionWrap.ts";
import { splitDigits, stripEmTags } from "./srt.ts";
import { createEstimateMeasurer } from "./textMeasure.ts";
import { CANVAS_WIDTH, type RenderBrand, type RenderLayout } from "../remotion/types.ts";

/** A measurer for (family, weight, size) — canvas, estimate or font file.
 *  The factory is expected to already apply the `textTransform: "uppercase"`
 *  measurement rule (measure the uppercased string), as CaptionLine's does. */
export type MeasureFor = (family: string, weight: number, sizePx: number) => MeasureFn;

export interface CaptionRows {
  /** Founding two-line punch treatment (top line + big accent line). */
  bigPunch: boolean;
  /** Punch style under wrapMode "auto": colour by LINE, rows re-balanced. */
  linePunch: boolean;
  /** Rows for every non-bigPunch path (uniform, linePunch, legacy collapse). */
  uniformLines: string[];
  /** bigPunch only — one array per authored line. */
  topLines: string[];
  bottomLines: string[];
  usableWidth: number;
  /** Last-row scale actually in force (1 unless uniform + lastLineScale). */
  lastScale: number;
  lastLinePx: number;
}

/** Usable caption row width. Stroke paints centered on the glyph outline —
 *  up to widthPx/2 beyond the measured advance per side — so it shrinks the
 *  wrap budget in auto mode only (the founding authored path is untouched). */
export function usableCaptionWidth(
  cap: RenderLayout["caption"],
  wrapAuto: boolean,
  canvasWidthPx: number = CANVAS_WIDTH,
): number {
  const strokeInflation = wrapAuto && cap.stroke ? cap.stroke.widthPx : 0;
  return canvasWidthPx - 2 * cap.sidePaddingPx - (cap.box ? 2 * cap.box.padXPx : 0) - strokeInflation;
}

/** Body-font measurer with digit runs priced in the punch font — mirrors
 *  renderWithDigitFont's font swap exactly. */
export function bodyMeasurerWithDigits(measFor: MeasureFor, b: RenderBrand, sizePx: number): MeasureFn {
  const body = measFor(b.captionFont, 700, sizePx);
  const punch = measFor(b.punchFont, 400, sizePx);
  return (t) =>
    splitDigits(t).reduce((w, seg) => w + (seg.isDigit ? punch(seg.text) : body(seg.text)), 0);
}

/** The row selection for ONE cue's text under a design. */
export function selectCaptionRows(
  text: string,
  cap: RenderLayout["caption"],
  b: RenderBrand,
  measFor: MeasureFor,
): CaptionRows {
  const wrapAuto = (cap.wrapMode ?? "authored") === "auto";
  const glyphRatio = b.glyphRatio ?? 0.6;
  const measBodyWithDigits = (sizePx: number): MeasureFn => bodyMeasurerWithDigits(measFor, b, sizePx);
  const stripEm = (s: string): string => stripEmTags(s);

  const lines = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  const capStyle = cap.captionStyle ?? "punch";
  // linePunch: colour the caption BY LINE — line 1 fully WHITE, line 2 fully
  // ACCENT (never scattered <em> words), and re-wrap the WHOLE cue into two
  // BALANCED lines (ignoring authored \n) so they read as similar lengths.
  // The operator's punch rule for multi-language channels. Only auto+punch
  // reaches it; Arabic ships wrapMode "authored" (wrapAuto false), so it keeps
  // the founding bigPunch path below, byte-identical.
  const linePunch = wrapAuto && capStyle !== "uniform";
  // Big-punch two-line treatment (founding / authored path): punch style, the
  // channel enables it (cap.twoLine != null) AND the cue has two authored
  // lines. Excludes linePunch (which owns punch coloring under auto).
  const bigPunch = !linePunch && capStyle !== "uniform" && !!cap.twoLine && lines.length >= 2;

  // Legacy collapse (punch style, twoLine:null channels): prefer ONE line.
  // If the joined text fits the usable width at the current font size,
  // collapse the cue's lines into one; otherwise keep them stacked (with
  // the channel's interline). Width is estimated from an average glyph
  // advance — 0.6em for Arabic (legacy default), narrower for Latin/LTR
  // scripts via the channel's glyphRatio. UNTOUCHED for byte-identity.
  // Stroke paints centered on the glyph outline — up to widthPx/2 beyond the
  // measured advance per side — so it shrinks the wrap budget in auto mode.
  const usableWidth = usableCaptionWidth(cap, wrapAuto);
  const approxCharW = cap.fontSizePx * (b.glyphRatio ?? 0.6);
  const maxCharsOneLine = Math.max(1, Math.floor(usableWidth / approxCharW));
  const joinedUniform = lines.join(" ");
  // Last-line size (spec 2026-09-16 round 2 §2.6): in UNIFORM style the LAST
  // row of a multi-row cue renders at round(fontSizePx × lastLineScale).
  // Ruling: it acts ONLY when capStyle is "uniform" — punch style (bigPunch
  // and the auto line-punch) is pinned to 1 here, so both stay byte-identical
  // whatever the field says (the UI hides the control for punch anyway).
  // 1 — absent, i.e. every existing channel — leaves BOTH the wrap and the
  // row style on the exact expressions below. Any other value routes the
  // same wrap through rowsWithScaledLastLine, so the bigger last row still
  // fits the frame (rows re-chosen at the scaled size only when that helps).
  const lastScale = capStyle === "uniform" ? cap.lastLineScale ?? 1 : 1;
  const lastLinePx = Math.round(cap.fontSizePx * lastScale);
  // UNIFORM style (Phase 5): explicit \n respected, over-budget lines
  // balanced-wrapped, em spans preserved — via the shared pure module.
  // wrapMode "auto" (v2): the SAME balanced wrap, but against the real
  // measured width — and it applies to PUNCH non-two-line cues as well,
  // so shrinking the side padding pushes text onto a new line live.
  /** The uniform-path wrap at a body size: `cap.fontSizePx` is the founding
   *  call; the scaled size is the re-wrap when the big last row overflows.
   *  The legacy punch collapse (last branch) does not depend on the size. */
  const wrapUniformAt = (fontPx: number): string[] => linePunch
    // Rejoin authored lines and re-wrap the whole cue BALANCED (max 2), on the
    // PLAIN text — coloring is by line below, so <em> is irrelevant here.
    ? wrapCueToWidth(stripEm(joinedUniform), usableWidth, measBodyWithDigits(fontPx), cap.maxLines ?? 2)
    : wrapAuto
    // maxLines is a TOTAL row cap when the channel pins it: the authored `\n`
    // is re-joined first, so a pre-broken cue can't render maxLines rows PER
    // authored line (spec 2026-09-15-brand-study-design.md §4). Without an
    // explicit maxLines the `?? 2` default keeps the founding per-authored-line
    // behaviour for existing wrapMode:"auto" channels, byte-identically.
    // `!= null`, not `!== undefined`: the patch layer uses null as its UNSET
    // signal and applyEditState deletes the key — but a null that ever leaked
    // through must read as "no cap", never cap the cue to a single line.
    ? cap.maxLines != null
      ? wrapCueTotalToWidth(text, usableWidth, measBodyWithDigits(fontPx), cap.maxLines)
      // No pinned cap: two rows are the PREFERENCE, not a promise. A cue that
      // fits in two still gets exactly two (byte-identical); one that cannot
      // gets the rows it needs instead of one merged row running off both
      // edges of the frame (QA 2026-09-16, B1 — the Brand Setup "Large" card,
      // and the panel's own "Lines · Let us decide").
      : wrapCuePreferring(text, usableWidth, measBodyWithDigits(fontPx), 2)
    : capStyle === "uniform"
    // The char budget follows the size, so the scaled call is the base budget
    // divided by the scale (modulo the floor).
    ? wrapUniformCue(text, uniformMaxChars({
        fontSizePx: fontPx,
        sidePaddingPx: cap.sidePaddingPx,
        boxPadXPx: cap.box?.padXPx ?? 0,
        glyphRatio: b.glyphRatio ?? 0.6,
        canvasWidthPx: CANVAS_WIDTH,
      }), cap.maxLines)
    : joinedUniform.length <= maxCharsOneLine ? [joinedUniform] : lines;
  const uniformLines = lastScale === 1
    ? wrapUniformAt(cap.fontSizePx)
    : rowsWithScaledLastLine(
        () => wrapUniformAt(cap.fontSizePx),
        () => wrapUniformAt(lastLinePx),
        usableWidth,
        // The overflow check measures the last row at the scaled size with
        // the model the wrap itself used: gated glyph measurement under
        // "auto"; otherwise the char-budget estimate — which ignores letter-
        // spacing (uniformMaxChars has no tracking term), so the probe passes
        // 0 too, or the two models would disagree at the edge. The canvas
        // measurer is not fonts-gated there, so it must not decide anything.
        wrapAuto ? measBodyWithDigits(lastLinePx) : createEstimateMeasurer(lastLinePx, glyphRatio, 0),
      );

  // Punch two-line grammar under "auto": each authored line wraps at ITS
  // OWN size/face when it overflows (top at twoLine.topPx in the body font,
  // punch line at twoLine.bottomPx in the punch font). Authored mode keeps
  // exactly one div per line — the founding path.
  // Each authored punch line stays on its OWN single row (maxLines 1) so a
  // 2-line cue can never spill to a third line — operator rule "max two lines".
  const topLines = bigPunch
    ? wrapAuto
      ? wrapCueToWidth(lines[0] ?? "", usableWidth, measBodyWithDigits(cap.twoLine!.topPx), 1)
      : [lines[0] ?? ""]
    : [];
  const bottomLines = bigPunch
    ? wrapAuto
      ? wrapCueToWidth(lines[1] ?? "", usableWidth, measFor(b.punchFont, 400, cap.twoLine!.bottomPx), 1)
      : [lines[1] ?? ""]
    : [];


  return { bigPunch, linePunch, uniformLines, topLines, bottomLines, usableWidth, lastScale, lastLinePx };
}
